import { Injectable } from '@angular/core';

export type KilnFirmwareSource = {
  firmware?: string;
  status?: { fw?: string };
};

@Injectable({
  providedIn: 'root',
})
export class FirmwareService {
  private static readonly REMOTE_STOP_MIN_SDI_VERSION = '1.14.15';
  private static readonly HEALTH_CHECK_MIN_SDI_VERSION = '1.14.15';
  private static readonly HEALTH_CHECK_MIN_GENESIS_VERSION = '1.15.0';

  /** Parsed semver from kiln.status.fw or kiln.firmware (e.g. SDI-1.14.15 → 1.14.15). */
  parseFirmwareVersion(firmwareString?: string): string | null {
    if (!firmwareString || typeof firmwareString !== 'string') {
      return null;
    }
    const trimmed = firmwareString.trim();
    const versionPart = trimmed.includes('-')
      ? trimmed.split('-').slice(1).join('-')
      : trimmed;
    const cleaned = versionPart.replace(/^(SDI|GSP)/i, '').trim();
    const match = cleaned.match(/(\d+\.\d+(?:\.\d+)?)/);
    return match ? match[1] : null;
  }

  compareFirmwareVersions(a: string, b: string): number {
    const toParts = (v: string) => v.split('.').map((n) => parseInt(n, 10) || 0);
    const pa = toParts(a);
    const pb = toParts(b);
    const len = Math.max(pa.length, pb.length, 3);
    for (let i = 0; i < len; i++) {
      const diff = (pa[i] || 0) - (pb[i] || 0);
      if (diff !== 0) {
        return diff;
      }
    }
    return 0;
  }

  isFirmwareVersionAtLeast(firmwareString: string | undefined, minimumVersion: string): boolean {
    const version = this.parseFirmwareVersion(firmwareString);
    if (!version) {
      return false;
    }
    return this.compareFirmwareVersions(version, minimumVersion) >= 0;
  }

  /** Health diagnostics require SDI ≥ 1.14.15 or Genesis/GSP ≥ 1.15.0. */
  supportsHealthCheck(kiln?: KilnFirmwareSource): boolean {
    const fw = kiln?.firmware || kiln?.status?.fw;
    if (!fw) {
      return false;
    }
    if (fw.includes('SDI')) {
      return this.isFirmwareVersionAtLeast(fw, FirmwareService.HEALTH_CHECK_MIN_SDI_VERSION);
    }
    if (fw.includes('GSP') || /Genesis/i.test(fw)) {
      return this.isFirmwareVersionAtLeast(fw, FirmwareService.HEALTH_CHECK_MIN_GENESIS_VERSION);
    }
    return false;
  }

  getHealthCheckMinimumFirmwareLabel(firmwareString?: string): string {
    if (firmwareString?.includes('SDI')) {
      return FirmwareService.HEALTH_CHECK_MIN_SDI_VERSION;
    }
    return FirmwareService.HEALTH_CHECK_MIN_GENESIS_VERSION;
  }

  /** Remote stop requires SDI firmware ≥ 1.14.15 (GSP and other lines are not supported). */
  supportsRemoteStopFiring(kiln?: KilnFirmwareSource): boolean {
    const fw = kiln?.firmware || kiln?.status?.fw;
    if (!fw || fw.includes('GSP') || !fw.includes('SDI')) {
      return false;
    }
    return this.isFirmwareVersionAtLeast(fw, FirmwareService.REMOTE_STOP_MIN_SDI_VERSION);
  }
}
